import React, { useState, useEffect, useCallback } from 'react';
import { View, LearningModule } from '../types';
import Card from './common/Card';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, ZoomInIcon } from './common/Icons';
import { formatAssetUrl } from '../services/apiService';

interface LearningModuleViewProps {
  onBack: () => void;
  /** Về thẳng trang Lộ trình (thay vì browser-back về module trước). */
  onBackToPlan?: () => void;
  onNavigate: (view: View, payload?: object) => void;
  planId: string;
  module: LearningModule;
  onComplete: (planId: string, moduleId: string, isCorrect: boolean) => void;
  isPreview?: boolean;
  // ── Ngữ cảnh chuỗi học (để điều hướng liền mạch + tiến độ) ──
  planTitle?: string;
  dayLabel?: string;
  posInDay?: number;            // vị trí trong ngày (1-based)
  dayModuleCount?: number;
  overallDone?: number;         // số bài đã hoàn thành trong cả lộ trình
  overallTotal?: number;
  nextModule?: LearningModule | null;
  nextDayLabel?: string;        // có giá trị khi bài kế thuộc NGÀY MỚI
  isLastOfDay?: boolean;        // bài kế bắt đầu ngày mới
  isLastOfPlan?: boolean;       // bài cuối cùng của lộ trình
  onGoToModule?: (m: LearningModule) => void;
}

const LearningModuleView: React.FC<LearningModuleViewProps> = ({
  onBack, onBackToPlan, module, planId, onComplete, isPreview = false,
  planTitle, dayLabel, posInDay, dayModuleCount,
  overallDone = 0, overallTotal = 0,
  nextModule, nextDayLabel, isLastOfDay = false, isLastOfPlan = false, onGoToModule,
}) => {
    // Mọi nút "về lộ trình" đi thẳng trang Lộ trình (không browser-back về bài trước).
    const backToPlan = onBackToPlan ?? onBack;
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);

    // Lưu vị trí đang học để "Tiếp tục" từ Lộ trình + cuộn lên đầu khi đổi bài.
    useEffect(() => {
        if (module?.id && planId) {
            sessionStorage.setItem('lastViewedModuleId', module.id);
            sessionStorage.setItem('lastViewedPlanId', String(planId));
        }
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [module?.id, planId]);

    const isCorrect = isSubmitted && selectedOption === module.question.correctAnswer;

    const handleSubmit = useCallback(() => {
        if (selectedOption === null || isSubmitted) return;
        setIsSubmitted(true);
        onComplete(planId, module.id, selectedOption === module.question.correctAnswer);
    }, [selectedOption, isSubmitted, onComplete, planId, module.id]);

    const goNext = useCallback(() => {
        if (nextModule && onGoToModule) onGoToModule(nextModule);
    }, [nextModule, onGoToModule]);

    // Phím tắt: Enter để Kiểm tra → rồi Enter để qua bài kế (luồng liền mạch, không rời chuột).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'TEXTAREA' || tag === 'INPUT') return;
            if (!isSubmitted) { if (selectedOption !== null) handleSubmit(); }
            else if (nextModule && !isLastOfDay) { goNext(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isSubmitted, selectedOption, nextModule, isLastOfDay, handleSubmit, goNext]);

    // Tiến độ cả lộ trình (tính cả bài vừa làm xong để cảm giác tiến lên ngay).
    const doneForBar = Math.min(overallTotal, overallDone + (isSubmitted && module.status !== 'completed' ? 1 : 0));
    const pct = overallTotal > 0 ? Math.round((doneForBar / overallTotal) * 100) : 0;

    return (
        <div className="max-w-3xl mx-auto space-y-5 pb-16">
            {/* ── Thanh tiến độ + ngữ cảnh (sticky) ── */}
            <div className="sticky top-0 z-30 -mx-4 px-4 py-3 bg-[#f8fafc]/90 backdrop-blur border-b border-slate-100">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <button onClick={backToPlan} className="flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition-colors">
                        <ArrowLeftIcon className="h-4 w-4" /> Lộ trình
                    </button>
                    <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500">
                        {dayLabel && <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full border border-teal-100">{dayLabel}</span>}
                        {posInDay && dayModuleCount && <span>Bài {posInDay}/{dayModuleCount}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap">{doneForBar}/{overallTotal} bài</span>
                </div>
            </div>

            {isPreview && (
                <div className="bg-amber-50 border-2 border-amber-200 p-4 rounded-2xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-xl text-amber-600"><span className="text-xl">⚠️</span></div>
                        <div>
                            <p className="font-black text-amber-800 uppercase text-xs tracking-wider">Chế độ xem trước của phụ huynh</p>
                            <p className="text-amber-600 text-[10px] font-bold">Bạn đang xem thử nội dung con sẽ học. Kết quả sẽ không ghi vào tiến độ của con.</p>
                        </div>
                    </div>
                    <span className="hidden sm:block px-3 py-1 bg-amber-600 text-white text-[10px] font-black uppercase rounded-lg">Xem thử</span>
                </div>
            )}

            {zoomImageUrl && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-90 p-4" onClick={() => setZoomImageUrl(null)}>
                    <button className="absolute top-4 right-4 text-white hover:text-gray-300"><XMarkIcon className="h-8 w-8" /></button>
                    <img src={zoomImageUrl} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" alt="Phóng to" />
                </div>
            )}

            <Card className="p-6 sm:p-8">
                {/* Nội dung bài học */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">AI</div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{module.title}</h2>
                    </div>
                    <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed bg-gray-50 p-5 sm:p-6 rounded-lg">
                        {module.imageUrl && (
                            <div className="mb-6 rounded-xl overflow-hidden shadow-md max-w-md mx-auto aspect-video relative group">
                                <img src={formatAssetUrl(module.imageUrl)} alt={module.title} className="w-full h-full object-cover" />
                                <button onClick={() => setZoomImageUrl(formatAssetUrl(module.imageUrl))} className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70">
                                    <ZoomInIcon className="h-5 w-5" />
                                </button>
                            </div>
                        )}
                        <p>{module.content}</p>
                    </div>
                </div>

                {/* Câu hỏi kiểm tra */}
                <div className="border-t pt-7">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Câu hỏi nhanh!</h3>
                    {module.question.image && (
                        <div className="mb-6 rounded-xl overflow-hidden shadow-sm max-w-md mx-auto aspect-video border bg-gray-50 relative group">
                            <img src={formatAssetUrl(module.question.image)} alt="Câu hỏi minh họa" className="w-full h-full object-cover" />
                            <button onClick={() => setZoomImageUrl(formatAssetUrl(module.question.image))} className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70">
                                <ZoomInIcon className="h-5 w-5" />
                            </button>
                        </div>
                    )}
                    <p className="text-lg font-medium text-gray-800 mb-6">{module.question.text}</p>
                    <div className={`grid gap-4 ${module.question.optionImages?.some(img => !!img) ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                        {module.question.options.map((option, index) => {
                            const isSelected = selectedOption === option;
                            const isCorrectAnswer = module.question.correctAnswer === option;
                            const optionImage = module.question.optionImages?.[index];
                            let buttonClass = 'border-gray-300 bg-white hover:bg-gray-100';
                            if (isSubmitted) {
                                if (isCorrectAnswer) buttonClass = 'border-green-500 bg-green-100 text-green-900 ring-2 ring-green-400';
                                else if (isSelected) buttonClass = 'border-red-500 bg-red-100 text-red-900 ring-2 ring-red-400';
                                else buttonClass = 'border-gray-300 bg-gray-50 text-gray-500';
                            } else if (isSelected) {
                                buttonClass = 'border-teal-500 bg-teal-50 ring-2 ring-teal-400';
                            }
                            return (
                                <button key={index} onClick={() => !isSubmitted && setSelectedOption(option)}
                                    className={`w-full text-left p-4 border-2 rounded-lg flex flex-col transition-all ${isSubmitted ? 'cursor-default' : 'cursor-pointer'} ${buttonClass}`}>
                                    {optionImage && (
                                        <div className="w-full aspect-video rounded-md overflow-hidden mb-3 border bg-gray-200 relative group/optimg">
                                            <img src={formatAssetUrl(optionImage)} alt={option} className="w-full h-full object-cover" />
                                            <button onClick={(e) => { e.stopPropagation(); setZoomImageUrl(formatAssetUrl(optionImage)); }} className="absolute top-1 right-1 p-1.5 bg-black/50 text-white rounded-full opacity-0 group-hover/optimg:opacity-100 transition-opacity hover:bg-black/70">
                                                <ZoomInIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between w-full">
                                        <span className="font-semibold">{option}</span>
                                        {isSubmitted && isCorrectAnswer && <CheckIcon className="h-6 w-6 text-green-600" />}
                                        {isSubmitted && !isCorrectAnswer && isSelected && <XMarkIcon className="h-6 w-6 text-red-600" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Kết quả + Điều hướng liền mạch */}
                <div className="mt-8">
                    {!isSubmitted ? (
                        <div className="text-center">
                            <button onClick={handleSubmit} disabled={selectedOption === null}
                                className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                                Kiểm tra
                            </button>
                            {selectedOption !== null && <p className="text-[10px] text-slate-400 mt-2">Mẹo: nhấn Enter để kiểm tra nhanh</p>}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Phản hồi đáp án */}
                            {isCorrect ? (
                                <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-xl text-center">
                                    <h4 className="font-bold">🎉 Chính xác! Làm tốt lắm!</h4>
                                </div>
                            ) : (
                                <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl text-center">
                                    <h4 className="font-bold">Chưa đúng rồi!</h4>
                                    <p className="text-sm mt-1">Đáp án đúng: <strong>{module.question.correctAnswer}</strong></p>
                                </div>
                            )}

                            {/* Hành động tiếp theo — tuỳ ngữ cảnh */}
                            {isLastOfPlan ? (
                                <div className="text-center bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100 rounded-2xl p-6">
                                    <div className="text-4xl mb-2">🏆</div>
                                    <h4 className="text-lg font-extrabold text-slate-900 mb-1">Hoàn thành lộ trình!</h4>
                                    <p className="text-xs text-slate-500 mb-4">Bạn đã hoàn thành tất cả {overallTotal} bài học. Tuyệt vời!</p>
                                    <button onClick={backToPlan} className="px-6 py-3 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-colors">
                                        Về lộ trình
                                    </button>
                                </div>
                            ) : isLastOfDay ? (
                                <div className="text-center bg-gradient-to-br from-indigo-50 to-teal-50 border border-teal-100 rounded-2xl p-6">
                                    <div className="text-3xl mb-2">✅</div>
                                    <h4 className="text-base font-extrabold text-slate-900 mb-1">Hoàn thành {dayLabel}!</h4>
                                    <p className="text-xs text-slate-500 mb-4">Bạn có thể học tiếp {nextDayLabel} ngay, hoặc tạm dừng và quay lại sau.</p>
                                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                                        <button onClick={goNext} className="px-6 py-3 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-colors">
                                            Học tiếp {nextDayLabel} →
                                        </button>
                                        <button onClick={backToPlan} className="px-6 py-3 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-colors">
                                            Tạm dừng tại đây
                                        </button>
                                    </div>
                                </div>
                            ) : nextModule ? (
                                <div className="text-center space-y-2">
                                    <button onClick={goNext} className="w-full sm:w-auto px-8 py-3.5 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-colors shadow-md shadow-teal-100 inline-flex items-center justify-center gap-2">
                                        Bài tiếp theo: {nextModule.title.length > 28 ? nextModule.title.slice(0, 28) + '…' : nextModule.title} →
                                    </button>
                                    <div>
                                        <button onClick={backToPlan} className="text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors mt-1">
                                            Quay lại lộ trình
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-slate-400">Mẹo: nhấn Enter để qua bài kế</p>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <button onClick={backToPlan} className="px-8 py-3 bg-slate-700 text-white font-semibold rounded-xl hover:bg-slate-800 transition-colors">
                                        Quay lại lộ trình
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default LearningModuleView;
